// Image tools guide — the right-rail Tools tab on image and video boxes: crop,
// border/box style, colour adjustments, filters, background removal / chroma
// key and structure (size, position, rotation, flip). Locale-keyed like every
// guide (content.<lang> → { feature, title, sections }).

const imageToolsGuide = {
  id: "image-tools",
  icon: "crop",
  keywords: [
    "tools",
    "araçlar",
    "crop",
    "kırp",
    "aspect ratio",
    "en boy oranı",
    "restore original",
    "orijinal haline döndür",
    "border",
    "kenarlık",
    "çerçeve",
    "corner radius",
    "köşe yuvarlaklığı",
    "circle",
    "tam yuvarlak",
    "padding",
    "iç boşluk",
    "opacity",
    "opaklık",
    "adjust",
    "renk ayarları",
    "brightness",
    "parlaklık",
    "contrast",
    "kontrast",
    "saturation",
    "doygunluk",
    "temperature",
    "sıcaklık",
    "blur",
    "bulanıklık",
    "invert",
    "ters çevir",
    "filters",
    "filtreler",
    "intensity",
    "yoğunluk",
    "remove background",
    "arka planı sil",
    "chroma key",
    "renk fonu sil",
    "green screen",
    "yeşil ekran",
    "structure",
    "yapı",
    "rotate",
    "döndür",
    "flip",
    "yansıt",
    "size",
    "boyut",
    "position",
    "konum",
    "image",
    "görsel",
    "video",
    "herramientas",
    "recortar",
    "relación de aspecto",
    "borde",
    "radio del borde",
    "relleno",
    "opacidad",
    "ajustes",
    "brillo",
    "contraste",
    "saturación",
    "desenfoque",
    "filtros",
    "quitar fondo",
    "clave de color",
    "pantalla verde",
    "estructura",
    "rotar",
    "voltear",
    "imagen",
    "vídeo",
    "floating toolbar",
    "hızlı araç çubuğu",
    "replace file",
    "dosyayı değiştir",
    "fit to screen",
    "ekrana sığdır",
    "more options",
    "daha fazla seçenek",
    "zoom",
    "yakınlaştır",
    "lock aspect ratio",
    "en boy oranını koru",
    "barra flotante",
    "reemplazar archivo",
    "ajustar a pantalla",
    "más opciones",
  ],
  content: {
    en: {
      feature: "Right menu",
      title: "Image tools",
      sections: [
        {
          id: "overview",
          title: "The Tools tab",
          videoUrl: null,
          text:
            "Select an image or a video on the canvas and click **Tools** in the right-side menu. For images and videos this tab takes the place of **Settings**. It shows a column of tool icons — hover one to see its name, click it to open its controls in a small panel, and click it again (or the close button) to close them.\n\n" +
            "- **Images** get every tool: **Crop**, **Border**, **Remove Background**, **Adjust**, **Filters** and **Structure**.\n" +
            "- **Videos** get **Border** and **Structure** only.\n" +
            "- Some elements also show **Marks list** (for images with marks) or **Pointer Events** here.\n" +
            "- The tab is not shown for interactive elements.",
        },
        {
          id: "crop",
          title: "Cropping",
          videoUrl: null,
          text:
            "Click **Crop** and a crop frame appears over the image on the canvas. Drag it freely, or pick an **Aspect Ratio** — **Free**, 1:1, 16:9, 9:16, 4:3, 3:4, 5:4 or 4:5 — to lock its shape. Confirm with the check button above the frame; the X button or Esc cancels.\n\n" +
            "- The image is replaced by the cropped area, placed exactly where the frame was. A single undo brings the uncropped image back.\n" +
            "- On a cropped image, **Restore image to original** appears in the crop panel. It brings back the full original image with its original size and position, even after several crops.",
        },
        {
          id: "border",
          title: "Border and box style",
          videoUrl: null,
          text:
            "- **Quick border** — one-click presets: **None**, **Sharp**, **Rounded**, **Soft**, **Pill**, **Circle**, **Dashed** and **Dotted**. **Circle** also makes the box square so the result is a true circle.\n" +
            "- **Padding** on each side and a **Background color**.\n" +
            "- **Border width**, **Border style** and **Border color**.\n" +
            "- **Border radius** for each corner separately.\n" +
            "- **Opacity** of the whole element.\n" +
            "- **Reset** returns all of these to their defaults.",
        },
        {
          id: "adjust-filters",
          title: "Adjust and Filters",
          videoUrl: null,
          text:
            "**Adjust** has sliders grouped as **White balance** (**Temperature**, **Tint**), **Light** (**Brightness**, **Contrast**), **Color** (**Vibrance**, **Saturation**, plus an **Invert** switch) and **Texture** (**Blur**).\n\n" +
            "**Filters** offers ready-made looks in groups such as Natural, Warm, Cold, Vintage, Mono, Cinematic, Pastel and Neon; each thumbnail previews the look on your own image. After picking one, use **Intensity** to tone it down. Click the active filter again to remove it.\n\n" +
            "- Adjustments and filters can be combined, and they show in the editor and in the published training.\n" +
            "- They don't change the image file itself — the original stays intact.\n" +
            "- **Reset settings** in each panel clears only that panel: the sliders, or the filter.",
        },
        {
          id: "remove-background",
          title: "Remove Background and Chroma Key",
          videoUrl: null,
          text:
            "Click **Remove Background** to open its panel, then **Confirm**:\n\n" +
            "- Without picking a colour, the background is detected and removed automatically.\n" +
            "- For a solid-colour background (such as a green screen), click **Chroma Key** first, then click the colour on the image. The picked colour is shown in the panel — the small × clears it. **Confirm** then removes that colour.\n\n" +
            "The removal runs in the background and replaces the image automatically when it is ready, without a separate preview. It is an AI job that may use credits; if your balance is too low you are told before it runs.",
        },
        {
          id: "structure",
          title: "Structure: size, position, rotation",
          videoUrl: null,
          text:
            "- **Width** and **Height** as a percentage. With **Lock aspect ratio** on, the height follows the width automatically.\n" +
            "- **X** and **Y** set the position as a percentage.\n" +
            "- **Rotate (deg)** sets the angle; the buttons next to it flip the element (**Horizontal Flip**, **Vertical Flip**) or reset rotation and flips.\n" +
            "- **Reset settings** here resets rotation and flips only — size and position are left as they are.",
        },
        {
          id: "quick-toolbar",
          title: "The floating image toolbar",
          videoUrl: null,
          text:
            "When you select an image on the canvas, a small toolbar floats above it with the most common actions — no need to open the right-side menu:\n\n" +
            "- **Replace File** — opens the file window so you can pick a different image.\n" +
            "- **Fit to Screen** — stretches the image to fill the whole scene (100% width and height, placed at the top-left corner).\n" +
            "- **Animate Image** — opens the Animate Image window (see the **AI Wizard** guide). For imported animated images or videos with no original image to start from, the button stays greyed out; hover it to see why.\n" +
            "- **More options** (the gear icon) — opens a small panel with the most-used structure settings.\n\n" +
            "In **More options**:\n\n" +
            "- **Lock aspect ratio** — keeps the proportions; the height switches to **Automatic** and follows the width. Holding Shift while dragging a resize handle switches it on or off temporarily.\n" +
            "- **Width** and **Height** as a percentage of the scene, and **X** and **Y** for the position, also as a percentage.\n" +
            "- **Rotation** — the angle in degrees, from −180° to 180°.\n" +
            "- **URL** — the address of the image file.\n" +
            "- **Zoom** — zooms the picture inside its frame without changing the frame's size.",
        },
      ],
    },
    tr: {
      feature: "Sağ Menü",
      title: "Görsel araçları",
      sections: [
        {
          id: "overview",
          title: "Araçlar sekmesi",
          videoUrl: null,
          text:
            "Tuvalde bir görsel veya video seçin ve sağ menüde **Araçlar**'a tıklayın. Görsel ve videolarda bu sekme **Ayarlar**'ın yerini alır. Bir araç simgeleri sütunu gösterir — adını görmek için simgenin üzerine gelin, ayarlarını küçük bir panelde açmak için tıklayın; kapatmak için yeniden (veya kapatma düğmesine) tıklayın.\n\n" +
            "- **Görsellerde** tüm araçlar bulunur: **Kırp**, **Kenarlık**, **Arka Planı Sil**, **Renk ayarları**, **Filtreler** ve **Yapı**.\n" +
            "- **Videolarda** yalnızca **Kenarlık** ve **Yapı** bulunur.\n" +
            "- Bazı öğelerde burada ayrıca **İşaretçi Listesi** (işaretçili görsellerde) veya **İşaretçi Olayları** da görünür.\n" +
            "- Sekme, etkileşimli öğelerde gösterilmez.",
        },
        {
          id: "crop",
          title: "Kırpma",
          videoUrl: null,
          text:
            "**Kırp**'a tıkladığınızda tuvaldeki görselin üzerinde bir kırpma çerçevesi belirir. Çerçeveyi serbestçe sürükleyin ya da şeklini sabitlemek için bir **En-Boy Oranı** seçin — **Serbest**, 1:1, 16:9, 9:16, 4:3, 3:4, 5:4 veya 4:5. Çerçevenin üstündeki onay düğmesiyle kırpın; X düğmesi veya Esc iptal eder.\n\n" +
            "- Görsel, çerçevenin bulunduğu yere yerleşen kırpılmış alanla değiştirilir. Tek bir geri alma kırpılmamış görseli geri getirir.\n" +
            "- Kırpılmış bir görselde kırpma panelinde **Görseli orijinal haline döndür** düğmesi görünür. Birkaç kez kırpılmış olsa bile, tam orijinal görseli ilk boyutu ve konumuyla geri getirir.",
        },
        {
          id: "border",
          title: "Kenarlık ve kutu stili",
          videoUrl: null,
          text:
            "- **Hızlı çerçeve** — tek tıkla uygulanan hazır ayarlar: **Yok**, **Keskin**, **Yuvarlak**, **Yumuşak**, **Hap**, **Tam yuvarlak**, **Kesikli** ve **Noktalı**. **Tam yuvarlak**, sonucun gerçek bir daire olması için kutuyu kare de yapar.\n" +
            "- Her kenar için **İç boşluk** ve bir **Arka plan rengi**.\n" +
            "- **Kenarlık kalınlığı**, **Kenarlık stili** ve **Kenarlık rengi**.\n" +
            "- Her köşe için ayrı **Köşe yuvarlaklığı**.\n" +
            "- Öğenin tamamı için **Opaklık**.\n" +
            "- **Sıfırla** bunların hepsini varsayılan değerlerine döndürür.",
        },
        {
          id: "adjust-filters",
          title: "Renk ayarları ve Filtreler",
          videoUrl: null,
          text:
            "**Renk ayarları**, **Beyaz dengesi** (**Sıcaklık**, **Renk Tonu**), **Aydınlık** (**Parlaklık**, **Kontrast**), **Renk** (**Canlılık**, **Doygunluk** ve bir **Ters çevir** anahtarı) ve **Doku** (**Bulanıklık**) olarak gruplanmış kaydırıcılar sunar.\n\n" +
            "**Filtreler**, Doğal, Sıcak, Soğuk, Vintage, Mono, Sinematik, Pastel ve Neon gibi gruplarda hazır görünümler sunar; her küçük resim görünümü kendi görselinizde önizler. Bir filtre seçtikten sonra etkisini azaltmak için **Yoğunluk**'u kullanın. Etkin filtreye yeniden tıklamak onu kaldırır.\n\n" +
            "- Renk ayarları ve filtreler birlikte kullanılabilir; hem editörde hem yayımlanan eğitimde görünür.\n" +
            "- Görsel dosyasının kendisini değiştirmezler — orijinal olduğu gibi kalır.\n" +
            "- Her paneldeki **Ayarları sıfırla** yalnızca o paneli temizler: kaydırıcıları ya da filtreyi.",
        },
        {
          id: "remove-background",
          title: "Arka Planı Sil ve Renk Fonu Sil",
          videoUrl: null,
          text:
            "Panelini açmak için **Arka Planı Sil**'e, ardından **Onayla**'ya tıklayın:\n\n" +
            "- Renk seçmeden onaylarsanız arka plan otomatik olarak algılanıp silinir.\n" +
            "- Düz renkli bir arka plan (örneğin yeşil ekran) için önce **Renk Fonu Sil**'e, sonra görseldeki renge tıklayın. Seçilen renk panelde gösterilir — yanındaki küçük × onu temizler. Ardından **Onayla** o rengi siler.\n\n" +
            "Silme işlemi arka planda çalışır ve hazır olduğunda ayrı bir önizleme olmadan görselin yerini otomatik olarak alır. Kredi harcayabilen bir yapay zekâ işidir; bakiyeniz yetersizse işlem çalışmadan önce bilgilendirilirsiniz.",
        },
        {
          id: "structure",
          title: "Yapı: boyut, konum, döndürme",
          videoUrl: null,
          text:
            "- Yüzde olarak **Genişlik** ve **Yükseklik**. **En boy oranını koru** açıkken yükseklik genişliği otomatik olarak takip eder.\n" +
            "- **X** ve **Y** konumu yüzde olarak belirler.\n" +
            "- **Döndür (Derece)** açıyı ayarlar; yanındaki düğmeler öğeyi yansıtır (**Yatay Yansıt**, **Dikey Yansıt**) ya da döndürme ve yansıtmayı sıfırlar.\n" +
            "- Buradaki **Ayarları sıfırla** yalnızca döndürme ve yansıtmayı sıfırlar — boyut ve konum olduğu gibi kalır.",
        },
        {
          id: "quick-toolbar",
          title: "Görselin üzerindeki hızlı araç çubuğu",
          videoUrl: null,
          text:
            "Tuvalde bir görsel seçtiğinizde, üzerinde en sık kullanılan işlemleri içeren küçük bir araç çubuğu belirir — sağ menüyü açmanız gerekmez:\n\n" +
            "- **Dosyayı Değiştir** — başka bir görsel seçebilmeniz için dosya penceresini açar.\n" +
            "- **Ekrana Sığdır** — görseli tüm sahneyi kaplayacak şekilde genişletir (%100 genişlik ve yükseklik, sol üst köşeye yerleşir).\n" +
            "- **Görseli Canlandır** — Görseli Canlandır penceresini açar (**AI Wizard** rehberine bakın). Başlangıç alınacak orijinal görseli olmayan, dışarıdan içe aktarılmış hareketli görsellerde veya videolarda düğme soluk görünür ve tıklanamaz; nedenini görmek için üzerine gelin.\n" +
            "- **Daha fazla seçenek** (dişli simgesi) — en sık kullanılan yapı ayarlarını içeren küçük bir panel açar.\n\n" +
            "**Daha fazla seçenek** panelinde:\n\n" +
            "- **En boy oranını koru** — oranları korur; yükseklik **Otomatik** olur ve genişliği takip eder. Bir boyutlandırma tutamacını sürüklerken Shift tuşunu basılı tutmak bunu geçici olarak açıp kapatır.\n" +
            "- Sahnenin yüzdesi olarak **Genişlik** ve **Yükseklik**; konum için yine yüzde olarak **X** ve **Y**.\n" +
            "- **Döndürme** — −180° ile 180° arasında derece cinsinden açı.\n" +
            "- **URL** — görsel dosyasının adresi.\n" +
            "- **Zoom** — çerçevenin boyutunu değiştirmeden görseli çerçevesinin içinde yakınlaştırır.",
        },
      ],
    },
    es: {
      feature: "Menú derecho",
      title: "Herramientas de imagen",
      sections: [
        {
          id: "overview",
          title: "La pestaña Herramientas",
          videoUrl: null,
          text:
            "Selecciona una imagen o un vídeo en el lienzo y haz clic en **Herramientas** en el menú derecho. En imágenes y vídeos, esta pestaña ocupa el lugar de **Ajustes**. Muestra una columna de iconos de herramientas: pasa el ratón sobre uno para ver su nombre, haz clic para abrir sus controles en un pequeño panel y vuelve a hacer clic (o usa el botón de cerrar) para cerrarlos.\n\n" +
            "- Las **imágenes** tienen todas las herramientas: **Recortar**, **Borde**, **Quitar fondo**, **Ajustes**, **Filtros** y **Estructura**.\n" +
            "- Los **vídeos** solo tienen **Borde** y **Estructura**.\n" +
            "- Algunos elementos también muestran aquí **Lista de Marcas** (en imágenes con marcas) o **Eventos de puntero**.\n" +
            "- La pestaña no se muestra en los elementos interactivos.",
        },
        {
          id: "crop",
          title: "Recorte",
          videoUrl: null,
          text:
            "Haz clic en **Recortar** y aparecerá un marco de recorte sobre la imagen en el lienzo. Arrástralo libremente o elige una **Relación de Aspecto** (**Libre**, 1:1, 16:9, 9:16, 4:3, 3:4, 5:4 o 4:5) para fijar su forma. Confirma con el botón de marca de verificación situado sobre el marco; el botón X o Esc cancela.\n\n" +
            "- La imagen se sustituye por el área recortada, colocada exactamente donde estaba el marco. Un solo deshacer recupera la imagen sin recortar.\n" +
            "- En una imagen recortada aparece **Restaurar la imagen original** en el panel de recorte. Recupera la imagen original completa con su tamaño y su posición originales, incluso después de varios recortes.",
        },
        {
          id: "border",
          title: "Borde y estilo del cuadro",
          videoUrl: null,
          text:
            "- **Borde rápido** — ajustes predefinidos de un solo clic: **Ninguno**, **Recto**, **Redondeado**, **Suave**, **Píldora**, **Círculo**, **Discontinuo** y **Punteado**. **Círculo** además hace cuadrado el cuadro para que el resultado sea un círculo perfecto.\n" +
            "- **Relleno** en cada lado y un **Color de fondo**.\n" +
            "- **Grosor del borde**, **Estilo del borde** y **Color del borde**.\n" +
            "- **Radio del borde** para cada esquina por separado.\n" +
            "- **Opacidad** de todo el elemento.\n" +
            "- **Restablecer** devuelve todos estos valores a los predeterminados.",
        },
        {
          id: "adjust-filters",
          title: "Ajustes y Filtros",
          videoUrl: null,
          text:
            "**Ajustes** tiene controles deslizantes agrupados en **Balance de blancos** (**Temperatura**, **Tono**), **Luz** (**Brillo**, **Contraste**), **Color** (**Vitalidad**, **Saturación**, además de un interruptor **Invertir**) y **Textura** (**Desenfoque**).\n\n" +
            "**Filtros** ofrece estilos predefinidos en grupos como Natural, Cálido, Frío, Vintage, Mono, Cinematográfico, Pastel y Neón; cada miniatura previsualiza el estilo sobre tu propia imagen. Después de elegir uno, usa **Intensidad** para suavizarlo. Haz clic de nuevo en el filtro activo para quitarlo.\n\n" +
            "- Los ajustes y los filtros se pueden combinar, y se muestran en el editor y en la formación publicada.\n" +
            "- No modifican el archivo de imagen: el original se mantiene intacto.\n" +
            "- **Restablecer ajustes** en cada panel borra solo ese panel: los controles deslizantes o el filtro.",
        },
        {
          id: "remove-background",
          title: "Quitar fondo y Clave de color",
          videoUrl: null,
          text:
            "Haz clic en **Quitar fondo** para abrir su panel y después en **Confirmar**:\n\n" +
            "- Si no eliges ningún color, el fondo se detecta y se elimina automáticamente.\n" +
            "- Para un fondo de color uniforme (como una pantalla verde), haz clic primero en **Clave de color** y después en el color de la imagen. El color elegido se muestra en el panel; la pequeña × lo borra. Después, **Confirmar** elimina ese color.\n\n" +
            "La eliminación se ejecuta en segundo plano y sustituye la imagen automáticamente cuando está lista, sin una vista previa aparte. Es una tarea de IA que puede consumir créditos; si tu saldo es insuficiente, se te avisa antes de que se ejecute.",
        },
        {
          id: "structure",
          title: "Estructura: tamaño, posición y rotación",
          videoUrl: null,
          text:
            "- **Ancho** y **Altura** en porcentaje. Con **Bloquear relación de aspecto** activado, la altura sigue automáticamente al ancho.\n" +
            "- **X** e **Y** definen la posición en porcentaje.\n" +
            "- **Rotar (º)** define el ángulo; los botones de al lado voltean el elemento (**Volteo horizontal**, **Volteo vertical**) o restablecen la rotación y los volteos.\n" +
            "- Aquí, **Restablecer ajustes** solo restablece la rotación y los volteos; el tamaño y la posición no cambian.",
        },
        {
          id: "quick-toolbar",
          title: "La barra flotante de la imagen",
          videoUrl: null,
          text:
            "Al seleccionar una imagen en el lienzo, aparece encima una pequeña barra flotante con las acciones más habituales, sin necesidad de abrir el menú derecho:\n\n" +
            "- **Reemplazar archivo**: abre la ventana de archivos para elegir otra imagen.\n" +
            "- **Ajustar a pantalla**: estira la imagen para que ocupe toda la escena (100 % de ancho y alto, colocada en la esquina superior izquierda).\n" +
            "- **Animar Imagen**: abre la ventana Animar Imagen (consulta la guía **AI Wizard**). En imágenes animadas o vídeos importados que no tienen una imagen original de partida, el botón aparece atenuado; pasa el ratón por encima para ver el motivo.\n" +
            "- **Más opciones** (el icono del engranaje): abre un pequeño panel con los ajustes de estructura más usados.\n\n" +
            "En **Más opciones**:\n\n" +
            "- **Bloquear relación de aspecto**: mantiene las proporciones; la altura pasa a **Automática** y sigue al ancho. Si mantienes Shift mientras arrastras un tirador de tamaño, se activa o desactiva temporalmente.\n" +
            "- **Ancho** y **Altura** en porcentaje de la escena, y **X** e **Y** para la posición, también en porcentaje.\n" +
            "- **Rotación**: el ángulo en grados, de −180° a 180°.\n" +
            "- **URL**: la dirección del archivo de imagen.\n" +
            "- **Zoom**: amplía la imagen dentro de su marco sin cambiar el tamaño del marco.",
        },
      ],
    },
  },
};

export default imageToolsGuide;
